<script setup>
import { reactive, onMounted, ref, getCurrentInstance, computed, watch, nextTick } from 'vue';
import SearchInput from './SearchInput.vue';
import BookerTracker from "../../../MetricsTracker/booker/metrics";
import { useHotelBookerStore } from '../../stores/hotelBooker';

const { proxy } = getCurrentInstance()
const __ = proxy.__
const config = window.__pt.settings.site;
const _tracker = new BookerTracker();
const searchInputRef = ref(null);
const hotelBookerStore = useHotelBookerStore();

const props = defineProps({
  recentSearches: { type: Array, default: () => [] },
  popularDestinations: { type: Array, default: () => [] },
  searchResults: { type: Array, default: () => [] },
  imgUrl: { type: String, default: '' },
  lastQuery: { type: String, default: '' },
  destination: { type: String, default: '' },
  product: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  icon: { type: String, default: 'icons-location' },
  origin: { type: String, default: '' },
  typeBooker: { type: String, default: '' },
})

const emit = defineEmits(['update:selectedDestination', 'update:query']);

const selectedDestination = reactive({
  place_name: "",
  place_selected: {}
});

const activeIndex = ref(-1);

// ------------------------
// Computed
// ------------------------
const selectableItems = computed(() => {
  if (props.searchResults.length > 0) {
    const flatResults = [];
    props.searchResults.forEach(item => {
      flatResults.push({ ...item, isAirport: false, parent: item });
      if (item.airports && item.airports.length > 0) {
        item.airports.forEach(airport => {
          flatResults.push({ ...airport, isAirport: true, parent: item });
        });
      }
    });
    return flatResults;
  }

  const defaults = [];
  if (props.recentSearches.length > 0) {
    defaults.push(...props.recentSearches.map(item => ({ ...item, isHistory: true })));
  }
  if (props.popularDestinations.length > 0) {
    defaults.push(...props.popularDestinations.map(item => ({ ...item, isPopular: true })));
  }

  return defaults;
});

// ------------------------
// Watchers
// ------------------------
watch(() => props.searchResults, () => {
  activeIndex.value = -1;
}, { deep: true });

watch(() => props.recentSearches, () => {
  activeIndex.value = -1;
}, { deep: true });

watch(() => props.popularDestinations, () => {
  activeIndex.value = -1;
}, { deep: true });

// ------------------------
// Constants / Static Maps
// ------------------------
const ICON_MAP = {
  3: 'icons-location',
  6: 'icons-globe-outline',
  7: 'icons-compass',
  11: 'icons-plane-airport',
  12: 'icons-monument',
  14: 'icons-hotel'
};

const DEFAULT_ICON = 'icons-globe-outline';

// ------------------------
// Helpers
// ------------------------
const getPlaceInfo = (product, item, isHistory = false, sugestions = false) => {
  if (!item) return { name: "", location: "" };
  switch (product) {
    case 'H': {
      const target = isHistory ? item.place_selected : item;
      return {
        name: target?.city || "",
        location: target?.location || "",
        code: target?.id || "",
        codeDestination: target?.id || ""
      };
 
    }
    case 'F':
      return {
        name: isHistory ? item.displayText : item.displayHtml,
        location: item.displayDestinationHtml || "",
        code: isHistory ? (item.type != 1 ? item.code : "") : (item.codeHtml && item.type != 1 ? item.codeHtml : ""),
        codeDestination: item.code
      };
    case 'P': {
        const isV1 = item.version === "v1";

        const isValidType = isV1
            ? item.placeTypeIcon !== 18
              : item.placeType !== 1;

        const code = isHistory
            ? (isValidType ? item.placeCode : "")
              : (item.placeCodeHtml && isValidType ? item.placeCodeHtml : "");

        const location = isV1
            ? (`${item.placeTypeIcon == 18 ? __('booker.all_airports') : ""} ${item.placeName || ""}`)
            : (item.placeName || "");

       return {
           name: isHistory ? item.placeDescription : item.placeDescriptionHtml,
           location: location,
           code: code,
           codeDestination: item.placeCode
       };
    }
    default:
      return { name: "", location: "" };
  }
};

const getIcon = (type) => ICON_MAP[type] || DEFAULT_ICON;

function onImgError(event) {
  let code = "us";
  if (config.siteName === "tiquetesbaratos") {
    code = "co";
  }
  event.target.src = `/assets/img/placeholder_img_${code}.png`;
}

// ------------------------
// Actions
// ------------------------
function onSearch(query) {
  activeIndex.value = -1;
  emit('update:query', query);
}

function handleKeyDown(event) {
  const items = selectableItems.value;
  if (items.length === 0) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % items.length;
    focusActiveItem();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + items.length) % items.length;
    focusActiveItem();
  } else if (event.key === 'Enter') {
    if (activeIndex.value >= 0 && activeIndex.value < items.length) {
      event.preventDefault();
      const item = items[activeIndex.value];
      onSelect(item, !!item.isHistory);
    }
  }
}

function focusActiveItem() {
  nextTick(() => {
    const container = document.querySelector('.searcher__body');
    const activeItem = container?.querySelector('.searcher__action.is-active');
    if (activeItem) {
      activeItem.focus();
    }
    scrollToActiveItem();
  });
}

function scrollToActiveItem() {
  nextTick(() => {
    const container = document.querySelector('.searcher__body');
    const activeItem = container?.querySelector('.searcher__action.is-active');
    if (activeItem && container) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      if (itemRect.bottom > containerRect.bottom) {
        activeItem.scrollIntoView({ block: 'end' });
      } else if (itemRect.top < containerRect.top) {
        activeItem.scrollIntoView({ block: 'start' });
      }
    }
  });
}

function onSelect(item, fromHistory = false, sufix = '') {
  let selectedPlaceName = "";
  let selectPlace = {};
  const { product, lastQuery } = props;

  if (product === 'H') {
    const target = fromHistory ? item.place_selected : item;
    selectedPlaceName = target.displayText;
    selectPlace = target;
  } else if (product === 'F') {
    selectedPlaceName = item.displayText;
    selectPlace = item;
  } else if (product === 'P') {
    selectedPlaceName = item.placeName;
    selectPlace = item;
  }
  emit('update:selectedDestination', {
    place_name: selectedPlaceName,
    place_selected: selectPlace,
    isNearby: sufix == '_nearby'
  });
  if (product === 'H') {
    if (fromHistory) {
      _tracker.metricRecentSearch(item.displayText || selectedPlaceName);
    }
    _tracker.metricAutocompleteHotelSelect(item, lastQuery, 'autocomplete_destination',sufix);
  } else if (product === 'F') {
    _tracker.metricAutocompleteFlightSelect(item, lastQuery, props.typeBooker, sufix);
  } else if (product === 'P') {
    _tracker.metricAutocompletePackageSelect(item, lastQuery, props.typeBooker, sufix);
  }
}

onMounted(() => {
  if (window.innerWidth < 768 && searchInputRef.value) {
    nextTick(() => {
      searchInputRef.value.focus();
    });
  }
});
</script>

<template>
  <div class="searcher">
    <!-- Input de búsqueda -->
    <div class="searcher__head">
      <SearchInput ref="searchInputRef" id="destination-search" :label="label" :selectOnFocus="true"
        :placeholder="placeholder" :value="destination" @input="onSearch" @keydown="handleKeyDown" :activeFocus="true"
        :debounce="700" :autocomplete="'off'" :spellcheck="false">
        <template #icon>
          <span class="icon font-icons font-20" :class="icon"></span>
        </template>
      </SearchInput>
    </div>

    <div class="searcher__body">
      <!-- Opción Hoteles Cercanos (Ubicación actual) -->
      <div v-if="product === 'H' && hotelBookerStore.nearbyLocation.show && searchResults.length == 0">
        <ul class="searcher__list">
          <li class="searcher__item">
            <button class="searcher__action" @click="onSelect(hotelBookerStore.nearbyLocation,false,'_nearby')" type="button">
              <span class="searcher__icon icons-location-dot-regular-full font-20"></span>
              <div class="searcher__info w-100 " >
                <p class="searcher__name">{{ __('booker.nearby_location') }}</p>
                <span class="searcher__loc location-name-nearby">{{ hotelBookerStore.nearbyLocation.name }}</span>
              </div>
            </button>
          </li>
        </ul>
      </div>

      <!-- Resultados de búsqueda -->
      <div v-if="searchResults.length" class="searcher__section">
        <ul class="searcher__list">
          <li v-if="searchResults.length === 0" class="searcher__empty">
            <p>{{ __('booker.no_destinations_found') }}</p>
          </li>
          <li v-for="(item, index) in searchResults" :key="`result-${index}`" class="searcher__item">
            <button class="searcher__action" @click="onSelect(item)" type="button"
              @keydown="handleKeyDown"
              :tabindex="activeIndex === selectableItems.findIndex(s => !s.isAirport && s.id === item.id) || (activeIndex === -1 && selectableItems.findIndex(s => !s.isAirport && s.id === item.id) === 0) ? 0 : -1"
              :class="{ 'is-active': selectableItems.findIndex(s => !s.isAirport && s.id === item.id) === activeIndex }">
              <span class="searcher__icon  font-20" :class="getIcon(item.type)"></span>
              <div class="searcher__info w-100">
                <div class="searcher__name-container">
                  <p class="searcher__name" v-html="getPlaceInfo(product, item).name"></p>
                  <span class="searcher__iata" v-show="getPlaceInfo(product, item).code && product != 'H'"
                    v-html="getPlaceInfo(product, item).code"></span>
                </div>

                <span class="searcher__loc" v-html="getPlaceInfo(product, item).location"></span>
              </div>
            </button>
            <button class="searcher__action pl-24" @click="onSelect(airport)" type="button"
              @keydown="handleKeyDown"
              v-for="(airport, aIndex) in item.airports" :key="`airport-${aIndex}`"
              :tabindex="activeIndex === selectableItems.findIndex(s => s.isAirport && s.id === airport.id && s.parent.id === item.id) ? 0 : -1"
              :class="{ 'is-active': selectableItems.findIndex(s => s.isAirport && s.id === airport.id && s.parent.id === item.id) === activeIndex }">
              <span class="searcher__icon searcher__icon--nested font-20 icons icons-subdirectory-arrow-right"></span>
              <div class="searcher__info w-100">
                <div class="searcher__name-container">
                  <p class="searcher__name" v-html="getPlaceInfo(product, airport).name"></p>
                  <span class="searcher__iata" v-show="getPlaceInfo(product, airport).code"
                    v-html="getPlaceInfo(product, airport).code"></span>
                </div>

                <span class="searcher__loc" v-html="getPlaceInfo(product, airport).location"></span>
              </div>
            </button>
          </li>
        </ul>
      </div>
      <!-- Búsquedas recientes -->
      <div v-else class="searcher__defaults">
        <div v-if="recentSearches.length > 0" class="searcher__section">
          <p class="searcher__title d-md-none ">{{ __('booker.recent_searches') }}</p>
          <ul class="searcher__list">
            <li v-for="(item, index) in recentSearches" :key="`recent-${index}`" class="searcher__item">
              <button class="searcher__action" @click="onSelect(item, true, '_recent')" type="button"
                @keydown="handleKeyDown"
                :tabindex="activeIndex === index || (activeIndex === -1 && index === 0) ? 0 : -1"
                :class="{ 'is-active': activeIndex === index }">
                <span class="searcher__icon icons-clock-outline font-20"></span>
                <div class="searcher__info w-100">
                  <div class="d-flex justify-content-between">
                    <p class="searcher__name" v-html="getPlaceInfo(product, item, true).name"></p>
                    <span class="searcher__iata" v-show="getPlaceInfo(product, item, true).code && product != 'H'"
                      v-html="getPlaceInfo(product, item, true).code"></span>
                  </div>
                  <span class="searcher__loc" v-html="getPlaceInfo(product, item, true).location"></span>
                </div>
              </button>
            </li>
          </ul>
        </div>
        <!-- Destinos populares -->
        <div v-if="popularDestinations.length > 0" class="searcher__section">
          <p class="searcher__title">{{ origin ? __('booker.popular_destinations_origin') :
            __('booker.popular_destinations') }}</p>
          <ul class="searcher__list">
            <li v-for="(item, index) in popularDestinations" :key="`pop-${index}`" class="searcher__item">
              <button class="searcher__action" @click="onSelect(item, false, '_top')" type="button"
                @keydown="handleKeyDown"
                :tabindex="activeIndex === index + recentSearches.length || (activeIndex === -1 && recentSearches.length === 0 && index === 0) ? 0 : -1"
                :class="{ 'is-active': activeIndex === index + recentSearches.length }">
                <figure class="searcher__figure">
                  <img :src="`${imgUrl}${getPlaceInfo(product, item, false).codeDestination}.jpg`" @error="onImgError" alt="" class="searcher__img" />
                </figure>
                <div class="searcher__info w-100">
                  <div class="d-flex justify-content-between">
                    <p class="searcher__name" v-html="getPlaceInfo(product, item).name"></p>
                    <span class="searcher__iata" v-show="getPlaceInfo(product, item, true).code && product != 'H'"
                      v-html="getPlaceInfo(product, item, true).code"></span>
                  </div>
                  <span class="searcher__loc" v-html="getPlaceInfo(product, item).location"></span>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Reset de botón para accesibilidad y visuales */
button {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.searcher {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  background-color: var(--bg-base);
  width: 100%;

  @media (min-width: 768px) {
    padding: var(--space-16);
    border-radius: var(--space-16);
    width: calc(100vw - 32px);
    max-width: 600px;
  }

  &__head {
    padding-bottom: var(--space-20);
    border-bottom: 1px solid var(--border-subtle);

    @media (min-width: 768px) {
      display: none;
    }
  }
}

.searcher__section {
  margin-bottom: var(--space-24);
}

.searcher__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-subtle);
  margin-bottom: var(--space-8);
  text-transform: uppercase;
}

.searcher__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.searcher__item {
  width: 100%;
  border-bottom: 1px solid var(--border-subtle);
}

.searcher__empty {
  padding: var(--space-16);
  color: var(--text-subtle);
  text-align: center;
  font-size: 0.9rem;
}

/* --- ELEMENTOS DE LISTA (Items) --- */
.searcher__action {
  display: flex;
  align-items: center;
  padding: var(--space-8);
  border-radius: var(--space-8);
  gap: var(--space-16);
  transition: background-color 0.2s ease;
}

.searcher__action:hover,
.searcher__action.is-active {
  background-color: var(--bg-level2);
  .searcher__name,
  .searcher__iata {
    font-weight: 600;
  }
}

/* --- ESTILOS DE ICONO (Usado en Recientes y Resultados) --- */
.searcher__icon {
  width: 57px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  /* Evita que se aplaste */
  border-radius: var(--space-8);

  &--nested {
    height: 30px;
    align-items: start;
  }
}

/* --- ESTILOS DE IMAGEN (Usado en Populares) --- */
.searcher__figure {
  margin: 0;
  width: 57px;
  height: 42px;
  border-radius: var(--space-8);
  overflow: hidden;
  flex-shrink: 0;
}

.searcher__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* --- TEXTOS --- */
.searcher__info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.searcher__name-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.searcher__name {
  font-weight: 400;
  font-size: 1rem;
  color: var(--text-strong);
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.searcher__loc {
  font-size: 0.875rem;
  color: var(--text-subtle);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
}

.searcher__iata {
  flex-shrink: 0;
  margin-left: var(--space-8);
}

/* --- BOTÓN FINAL --- */
.searcher__submit {
  padding: var(--space-16);
  background-color: #000;
  color: var(--text-oncolor);
  border-radius: var(--space-8);
  font-weight: bold;
  text-align: center;
  display: block;
  /* Asegura que ocupe el ancho definido */
}
</style>
